import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import EditConfirmationModal from '../../components/common/EditConfirmationModal';
import TitleWithBack from '../../components/common/TitleWithBack';

function ModulesEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    moduleName: '',
    moduleCode: '',
    examType: '',
    program: '',
    semester: '',
    yearOfStudy: '',
    requiredQuestionsCount: '',
    maxScore: ''
  });
  const [tooltip, setTooltip] = useState('');
  const [programs, setPrograms] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [yearsOfStudy, setYearsOfStudy] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [modular, setModular] = useState(false);

  useEffect(() => {
    async function fetchData() {
      try {
        const [progRes, examTypeRes, semRes, yosRes, moduleRes] = await Promise.all([
          axios.get('/api/programs'),
          axios.get('/api/exam-types'),
          axios.get('/api/semesters'),
          axios.get('/api/years-of-study'),
          axios.get(`/api/modules/${id}`)
        ]);
        setPrograms(Array.isArray(progRes.data) ? progRes.data : (progRes.data.data || []));
        setExamTypes(Array.isArray(examTypeRes.data) ? examTypeRes.data : (examTypeRes.data.data || []));
        setSemesters(Array.isArray(semRes.data) ? semRes.data : (semRes.data.data || []));
        setYearsOfStudy(Array.isArray(yosRes.data) ? yosRes.data : (yosRes.data.data || []));
        const data = moduleRes.data;
        setForm({
          moduleName: data.examName || data.moduleName,
          moduleCode: data.examId ? data.examId.toString() : (data.moduleCode || ''),
          examType: data.examType && data.examType.id ? data.examType.id.toString() : '',
          program: data.program?.id || '',
          semester: data.semester?.id || data.semester || '',
          yearOfStudy: data.yearOfStudy?.id || '',
          requiredQuestionsCount: data.requiredQuestionsCount != null ? data.requiredQuestionsCount.toString() : '',
          maxScore: data.maxScore != null ? data.maxScore.toString() : ''
        });
        setModular(data.program?.structureType === 'MODULAR');
      } catch (err) {
        setTooltip('Error fetching module details.');
      }
    }
    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleProgramChange = (e) => {
    const value = e.target.value;
    setForm(prev => ({ ...prev, program: value }));
    const selected = programs.find(p => String(p.id) === String(value));
    setModular(selected?.structureType === 'MODULAR');
  };

  const selectedExamTypeName = examTypes.find(et => et.id === parseInt(form.examType, 10))?.name;
  const isScoringGuideExamType = selectedExamTypeName === 'RLP' || selectedExamTypeName === 'OSM';

  const updateModule = async () => {
    const selectedProgram = programs.find(p => String(p.id) === String(form.program));
    const payload = {
      moduleName: form.moduleName,
      moduleCode: form.moduleCode,
      examType: { id: parseInt(form.examType, 10) },
      program: { id: parseInt(form.program, 10) },
      semester: modular ? null : { id: parseInt(form.semester, 10) },
      yearOfStudy: modular ? null : { id: parseInt(form.yearOfStudy, 10) },
      requiredQuestionsCount: isScoringGuideExamType && form.requiredQuestionsCount ? parseInt(form.requiredQuestionsCount, 10) : null,
      maxScore: isScoringGuideExamType && form.maxScore ? parseInt(form.maxScore, 10) : null
    };
    try {
      if (selectedProgram && modular !== (selectedProgram.structureType === 'MODULAR')) {
        await axios.put(`/api/programs/${form.program}/structure-type`, {
          structureType: modular ? 'MODULAR' : 'SEMESTER'
        });
      }
      await axios.put(`/api/modules/${id}`, payload);
      setTooltip('Module updated successfully!');
      setTimeout(() => navigate('/admin/modules/view'), 2000);
    } catch (err) {
      setTooltip('Error updating module.');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowConfirm(true);
  };

  return (
    <div className="container mt-2">
      <TitleWithBack title="Edit Module" />
      <div className="card shadow-sm">
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label">Module Name</label>
              <input 
                type="text" 
                className="form-control"
                name="moduleName"
                value={form.moduleName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Module Code</label>
              <input 
                type="text" 
                className="form-control"
                name="moduleCode"
                value={form.moduleCode}
                onChange={handleChange}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Exam Type</label>
              <select 
                className="form-select"
                name="examType"
                value={form.examType}
                onChange={handleChange}
                required
              >
                <option value="">Select Exam Type</option>
                {examTypes.map(et => (
                  <option key={et.id} value={et.id}>{et.name}</option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Program</label>
              <select
                className="form-select"
                name="program"
                value={form.program}
                onChange={handleProgramChange}
                required
              >
                <option value="">Select Program</option>
                {programs.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="mb-3 form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="modularCheck"
                checked={modular}
                onChange={(e) => setModular(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="modularCheck">
                This program is Modular (no Year of Study / Semester)
              </label>
              <div className="form-text">This applies to the entire program, not just this module.</div>
            </div>
            {!modular && (
              <>
                <div className="mb-3">
                  <label className="form-label">Semester</label>
                  <select
                    className="form-select"
                    name="semester"
                    value={form.semester}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Semester</option>
                    {semesters.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Year Of Study</label>
                  <select
                    className="form-select"
                    name="yearOfStudy"
                    value={form.yearOfStudy}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Year Of Study</option>
                    {yearsOfStudy.map(yos => (
                      <option key={yos.id} value={yos.id}>{yos.name}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
            {isScoringGuideExamType && (
              <>
                <div className="mb-3">
                  <label className="form-label">Questions a Candidate Must Attempt</label>
                  <input
                    type="number"
                    min="1"
                    className="form-control"
                    name="requiredQuestionsCount"
                    value={form.requiredQuestionsCount}
                    onChange={handleChange}
                    placeholder="Leave blank to require every question on the scoring guide"
                  />
                  <div className="form-text">
                    For RLP/OSM modules where a candidate isn't required to attempt every question. Leave blank
                    if every question on the scoring guide is mandatory.
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Maximum Score</label>
                  <input
                    type="number"
                    min="1"
                    className="form-control"
                    name="maxScore"
                    value={form.maxScore}
                    onChange={handleChange}
                    placeholder="Leave blank to use the scoring guide's own total"
                  />
                  <div className="form-text">
                    The fixed total used to calculate a candidate's final percentage, independent of how many
                    questions the scoring guide has. Leave blank to use the sum of the scoring guide's max
                    scores instead.
                  </div>
                </div>
              </>
            )}
            <button type="submit" className="btn btn-primary">Update Module</button>
          </form>
        </div>
      </div>
      {tooltip && (
        <div className={`mt-3 alert ${tooltip.startsWith('Error') ? 'alert-danger' : 'alert-success'}`}>
          {tooltip}
        </div>
      )}
      {showConfirm && (
        <EditConfirmationModal 
          title="Confirm Update"
          message="Are you sure you want to update this module?"
          onConfirm={() => { setShowConfirm(false); updateModule(); }}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </div>
  );
}

export default ModulesEdit;
